import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import {
  fetchShipment, fetchChecklistByShipment, fetchValidationRun,
  runValidation, fetchAiGuidance, previewReport, markItemUploaded
} from '../services/shipments';
import StatusBadge from '../components/shipments/StatusBadge';
import {
  ArrowLeft, FileText, ListChecks, AlertTriangle, BookOpenCheck,
  ShieldAlert, ShieldCheck, PlayCircle, Upload, CheckCircle2, FileDown, Sparkles
} from 'lucide-react';

const TABS = [
  { id: 'summary', label: 'Summary', icon: FileText },
  { id: 'issues', label: 'Issues', icon: AlertTriangle },
  { id: 'documents', label: 'Documents', icon: ListChecks },
  { id: 'guidance', label: 'Source-backed guidance', icon: BookOpenCheck },
  { id: 'report', label: 'Report preview', icon: FileDown },
];

export default function ShipmentResults() {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab') || 'summary';

  const [shipment, setShipment] = useState(null);
  const [checklist, setChecklist] = useState(null);
  const [validation, setValidation] = useState(null);
  const [running, setRunning] = useState(false);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const s = await fetchShipment(id);
      setShipment(s);
      const [c, v] = await Promise.allSettled([fetchChecklistByShipment(id), fetchValidationRun(id)]);
      setChecklist(c.status === 'fulfilled' ? c.value : null);
      setValidation(v.status === 'fulfilled' ? v.value : null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    reload();
  }, [reload]);

  const setTab = (t) => {
    searchParams.set('tab', t);
    setSearchParams(searchParams);
  };

  const onRunValidation = async () => {
    setRunning(true);
    try {
      const v = await runValidation(id);
      setValidation(v);
      const s = await fetchShipment(id);
      setShipment(s);
      setTab('issues');
    } finally {
      setRunning(false);
    }
  };

  if (loading || !shipment) {
    return <div className="text-slate-400 text-sm" data-testid="shipment-loading">Loading shipment…</div>;
  }

  return (
    <div className="space-y-8" data-testid="shipment-results-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <Link to="/shipments" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white mb-3" data-testid="back-to-shipments">
            <ArrowLeft className="h-4 w-4" /> All shipments
          </Link>
          <div className="text-xs uppercase tracking-widest text-cyan-glow font-mono mb-1">Pre-shipment review</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">{shipment.product_name}</h1>
          <div className="text-sm text-slate-400 mt-2 font-mono">
            {shipment.reference} · {shipment.origin_country} → {shipment.destination_country} · {shipment.mode}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <StatusBadge status={shipment.status} />
          <button
            onClick={onRunValidation}
            disabled={running}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-cyan-glow text-navy-950 font-semibold hover:bg-cyan-hover disabled:opacity-60 transition-colors"
            data-testid="run-validation-btn"
          >
            <PlayCircle className="h-4 w-4" />
            {running ? 'Running…' : 'Run validation'}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-white/10 -mx-1">
        <div className="flex gap-1 overflow-x-auto px-1">
          {TABS.map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`tab-${t.id}`}
              className={`inline-flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                tab === t.id
                  ? 'border-cyan-glow text-white'
                  : 'border-transparent text-slate-400 hover:text-white'
              }`}
            >
              <t.icon className="h-4 w-4" />
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab content */}
      {tab === 'summary' && <SummaryTab shipment={shipment} validation={validation} checklist={checklist} />}
      {tab === 'issues' && <IssuesTab validation={validation} />}
      {tab === 'documents' && <DocumentsTab checklist={checklist} onUpdate={setChecklist} />}
      {tab === 'guidance' && <GuidanceTab shipmentId={id} />}
      {tab === 'report' && <ReportTab shipmentId={id} />}
    </div>
  );
}

function SummaryTab({ shipment, validation, checklist }) {
  const uploadedCount = checklist?.items?.filter(i => i.uploaded).length || 0;
  const totalCount = checklist?.items?.length || 0;
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="tab-content-summary">
      <div className="lg:col-span-2 space-y-6">
        <Card title="Validation summary">
          <p className="text-slate-300 leading-relaxed">{validation?.summary || 'No validation has been run for this shipment yet.'}</p>
          <div className="text-xs text-slate-500 font-mono mt-4">AI interprets · Rules decide · Humans act</div>
        </Card>

        <Card title="Shipment details">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
            <KV label="Reference" value={shipment.reference} mono />
            <KV label="Origin" value={shipment.origin_country} />
            <KV label="Destination" value={shipment.destination_country} />
            <KV label="Mode" value={shipment.mode} />
            <KV label="Incoterm" value={shipment.incoterm} />
            <KV label="Quantity" value={shipment.quantity} />
            <KV label="HS Code" value={shipment.hs_code} mono />
            <KV label="Created" value={new Date(shipment.created_at).toLocaleDateString()} />
          </div>
        </Card>
      </div>

      <div className="space-y-4">
        <Card title="Checklist progress">
          <div className="flex items-baseline gap-2 mb-3">
            <span className="font-display text-3xl font-bold">{uploadedCount}</span>
            <span className="text-slate-400 text-sm">/ {totalCount} documents</span>
          </div>
          <div className="h-2 rounded-full bg-navy-800 overflow-hidden">
            <div className="h-full bg-cyan-glow" style={{ width: `${totalCount ? (uploadedCount/totalCount)*100 : 0}%` }} />
          </div>
          <Link to={`/shipments/${shipment.id}?tab=documents`} className="text-xs text-cyan-glow hover:text-cyan-hover mt-3 inline-block" data-testid="summary-view-docs">
            View documents →
          </Link>
        </Card>

        <Card title="Open issues" testid="summary-open-issues">
          {validation?.issues?.length ? (
            <ul className="space-y-2">
              {validation.issues.slice(0, 3).map(i => (
                <li key={i.id} className="text-sm text-slate-300 flex items-start gap-2">
                  {i.severity === 'blocking' ? <ShieldAlert className="h-4 w-4 text-red-400 shrink-0 mt-0.5" /> : <AlertTriangle className="h-4 w-4 text-amber-400 shrink-0 mt-0.5" />}
                  <span>{i.title}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-slate-400">No issues detected.</p>
          )}
        </Card>
      </div>
    </div>
  );
}

function IssuesTab({ validation }) {
  if (!validation || !validation.issues?.length) {
    return (
      <div className="rounded-xl border border-white/10 bg-navy-900 p-10 text-center" data-testid="tab-content-issues">
        <ShieldCheck className="h-10 w-10 text-emerald-400 mx-auto mb-3" />
        <div className="font-display text-xl text-white">No issues detected</div>
        <p className="text-sm text-slate-400 mt-1">All documents reconcile and required certifications are present.</p>
      </div>
    );
  }
  return (
    <div className="space-y-3" data-testid="tab-content-issues">
      {validation.issues.map(i => (
        <div key={i.id} className="rounded-xl border border-white/10 bg-navy-900 p-6" data-testid={`issue-${i.severity}`}>
          <div className="flex items-start gap-4">
            <div className={`h-10 w-10 rounded-lg grid place-items-center shrink-0 ${
              i.severity === 'blocking' ? 'bg-red-500/10 border border-red-500/20 text-red-400' :
              'bg-amber-500/10 border border-amber-500/20 text-amber-400'
            }`}>
              {i.severity === 'blocking' ? <ShieldAlert className="h-5 w-5" /> : <AlertTriangle className="h-5 w-5" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 mb-1">
                <span className={`text-[10px] uppercase tracking-widest font-mono ${
                  i.severity === 'blocking' ? 'text-red-400' : 'text-amber-400'
                }`}>{i.severity}</span>
                {i.documents_involved.length > 0 && (
                  <span className="text-xs text-slate-500 font-mono">{i.documents_involved.join(' · ')}</span>
                )}
              </div>
              <div className="font-display text-lg font-semibold text-white">{i.title}</div>
              <p className="text-sm text-slate-300 mt-1.5 leading-relaxed">{i.detail}</p>
              {i.recommendation && (
                <div className="mt-3 p-3 rounded-md bg-cyan-glow/5 border border-cyan-glow/15 text-sm text-cyan-glow/90">
                  <span className="font-semibold">Recommendation: </span>{i.recommendation}
                </div>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function DocumentsTab({ checklist, onUpdate }) {
  if (!checklist) return <div className="text-sm text-slate-400" data-testid="tab-content-documents">No checklist available.</div>;

  const onUpload = async (itemId) => {
    const updated = await markItemUploaded(checklist.id, itemId);
    onUpdate(updated);
  };

  return (
    <div className="space-y-3" data-testid="tab-content-documents">
      {checklist.items.map(item => (
        <div key={item.id} className="rounded-xl border border-white/10 bg-navy-900 p-5 flex items-center gap-4" data-testid={`doc-row-${item.document_name.toLowerCase().replace(/\s+/g, '-')}`}>
          <div className={`h-10 w-10 rounded-lg grid place-items-center shrink-0 ${
            item.status === 'pass' ? 'bg-emerald-500/10 border border-emerald-500/20 text-emerald-400' :
            item.status === 'warning' ? 'bg-amber-500/10 border border-amber-500/20 text-amber-400' :
            item.status === 'blocking' ? 'bg-red-500/10 border border-red-500/20 text-red-400' :
            'bg-slate-500/10 border border-slate-500/20 text-slate-400'
          }`}>
            {item.uploaded ? <CheckCircle2 className="h-5 w-5" /> : <FileText className="h-5 w-5" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <div className="font-medium text-white">{item.document_name}</div>
              {item.required ? (
                <span className="text-[10px] uppercase tracking-widest font-mono text-red-400/80">Required</span>
              ) : (
                <span className="text-[10px] uppercase tracking-widest font-mono text-slate-500">Recommended</span>
              )}
            </div>
            {item.notes && <p className="text-xs text-slate-400 mt-0.5">{item.notes}</p>}
          </div>
          <div className="flex items-center gap-3">
            {item.uploaded ? (
              <span className="text-xs text-emerald-400 font-mono">Uploaded</span>
            ) : (
              <button
                onClick={() => onUpload(item.id)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs border border-white/15 hover:bg-white/5 transition-colors"
                data-testid={`upload-${item.document_name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <Upload className="h-3.5 w-3.5" />
                Upload
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function GuidanceTab({ shipmentId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [askLoading, setAskLoading] = useState(false);
  const [question, setQuestion] = useState('');
  const [error, setError] = useState(null);

  const load = useCallback(async (q) => {
    if (q) setAskLoading(true); else setLoading(true);
    setError(null);
    try {
      const data = await fetchAiGuidance(shipmentId, q);
      setItems(data.guidance);
    } catch (e) {
      setError('Could not load AI guidance.');
    } finally {
      setLoading(false);
      setAskLoading(false);
    }
  }, [shipmentId]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="space-y-5" data-testid="tab-content-guidance">
      <div className="rounded-xl border border-white/10 bg-navy-900 p-5">
        <div className="flex items-center gap-2 text-xs text-slate-400 mb-3">
          <Sparkles className="h-3.5 w-3.5 text-cyan-glow" />
          <span className="uppercase tracking-widest font-mono">Ask the assistant</span>
        </div>
        <div className="flex gap-3">
          <input
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder="e.g. What documents are needed for organic Moringa exports to the EU?"
            className="flex-1 bg-navy-950 border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:border-cyan-glow/50 focus:outline-none focus:ring-2 focus:ring-cyan-glow/20"
            data-testid="guidance-input"
          />
          <button
            onClick={() => load(question)}
            disabled={askLoading}
            className="px-4 py-2.5 rounded-lg bg-cyan-glow text-navy-950 font-semibold hover:bg-cyan-hover disabled:opacity-60 text-sm"
            data-testid="guidance-ask-btn"
          >
            {askLoading ? 'Thinking…' : 'Ask'}
          </button>
        </div>
        <p className="text-xs text-slate-500 mt-3 font-mono">AI interprets, rules decide, humans act. Always confirm with regulatory sources.</p>
      </div>

      {error && <div className="text-sm text-red-400">{error}</div>}

      {loading ? (
        <div className="text-sm text-slate-400">Loading guidance…</div>
      ) : (
        <div className="space-y-3">
          {items.map((g, i) => (
            <div key={i} className="rounded-xl border border-white/10 bg-navy-900 p-6" data-testid={`guidance-item-${i}`}>
              <div className="flex items-start gap-4">
                <div className="h-10 w-10 rounded-lg bg-cyan-glow/10 border border-cyan-glow/20 grid place-items-center text-cyan-glow shrink-0">
                  <BookOpenCheck className="h-5 w-5" />
                </div>
                <div>
                  <div className="font-display text-base font-semibold text-white">{g.title}</div>
                  <div className="text-xs text-slate-500 font-mono mt-1">Source: {g.source}</div>
                  <p className="text-sm text-slate-300 mt-2 leading-relaxed">{g.summary}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ReportTab({ shipmentId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    previewReport(shipmentId).then(setData).finally(() => setLoading(false));
  }, [shipmentId]);

  if (loading) return <div className="text-sm text-slate-400" data-testid="tab-content-report">Loading report…</div>;
  if (!data) return null;

  const { shipment, validation_run } = data;

  return (
    <div className="rounded-xl border border-white/10 bg-white text-navy-950 p-8 md:p-12 max-w-4xl mx-auto" data-testid="tab-content-report">
      <div className="flex items-start justify-between mb-8 pb-6 border-b border-slate-200">
        <div>
          <div className="text-[10px] uppercase tracking-widest font-mono text-slate-500">Pre-shipment compliance report</div>
          <h2 className="font-display text-3xl font-bold mt-2 tracking-tight">{shipment.product_name}</h2>
          <div className="text-sm text-slate-600 font-mono mt-1">{shipment.reference}</div>
        </div>
        <div>
          <div className="text-xs font-mono text-slate-500">Generated</div>
          <div className="text-sm font-medium">{new Date().toLocaleDateString()}</div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-8 text-sm">
        <RKV label="Origin" value={shipment.origin_country} />
        <RKV label="Destination" value={shipment.destination_country} />
        <RKV label="Mode" value={shipment.mode} />
        <RKV label="HS Code" value={shipment.hs_code || '—'} mono />
      </div>

      <div className="mb-8">
        <h3 className="font-display text-lg font-bold mb-3">Validation outcome</h3>
        <div className="text-sm text-slate-700 leading-relaxed">{validation_run?.summary || 'No validation data available.'}</div>
      </div>

      {validation_run?.issues?.length > 0 && (
        <div className="mb-8">
          <h3 className="font-display text-lg font-bold mb-3">Findings</h3>
          <ul className="space-y-3">
            {validation_run.issues.map(i => (
              <li key={i.id} className="flex items-start gap-3 text-sm">
                <span className={`mt-0.5 h-2 w-2 rounded-full ${i.severity === 'blocking' ? 'bg-red-500' : 'bg-amber-500'}`} />
                <div>
                  <div className="font-semibold">{i.title}</div>
                  <div className="text-slate-600">{i.detail}</div>
                  {i.recommendation && <div className="text-slate-700 mt-1"><b>Recommendation:</b> {i.recommendation}</div>}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="text-[10px] text-slate-500 font-mono leading-relaxed pt-6 border-t border-slate-200">
        {data.disclaimer}
      </div>
    </div>
  );
}

function Card({ title, children, testid }) {
  return (
    <div className="rounded-xl border border-white/10 bg-navy-900 p-6" data-testid={testid}>
      <div className="text-xs uppercase tracking-widest text-slate-500 font-mono mb-4">{title}</div>
      {children}
    </div>
  );
}

function KV({ label, value, mono }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">{label}</div>
      <div className={`mt-1 text-sm text-white ${mono ? 'font-mono' : ''}`}>{value || '—'}</div>
    </div>
  );
}

function RKV({ label, value, mono }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">{label}</div>
      <div className={`mt-1 text-sm text-slate-900 ${mono ? 'font-mono' : ''}`}>{value || '—'}</div>
    </div>
  );
}
